import {
  Company_Create,
  Company_Read,
  Company_Update,
  deleteV1CompaniesId,
  getV1Companies,
  getV1CompaniesId,
  patchV1CompaniesId,
  postV1Companies,
  postV1CompaniesCompanyIdLogo,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { AdminCompany } from '~hooks/admin/useCompaniesAdmin/models';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { isUploadValidationError } from '~utils/extractAxiosError';

interface GetAllCompaniesParams {
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface UploadLogoParams {
  id: string;
  file: File;
}

export default function useCompaniesAdmin() {
  const { createDirectUpload } = useDirectUploads();
  const getAllCompanies = useCallback(
    async ({ cursor, limit, link, searchQuery, signal }: GetAllCompaniesParams) => {
      try {
        const response = await getV1Companies({
          query: {
            'page[limit]': limit,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
            ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
          },
          signal,
        });

        return {
          data: response.data.data.map((company: Company_Read) =>
            AdminCompany.parse(company),
          ),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(error);
        throw new Error(`Unable to retrieve companies`);
      }
    },
    [],
  );
  const getCompanyById = useCallback(async (id: string) => {
    try {
      const response = await getV1CompaniesId({ path: { id } });

      return AdminCompany.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company.failed_to_fetch_company'),
      );
      throw error;
    }
  }, []);
  const createCompany = useCallback(async (company: AdminCompany) => {
    try {
      const response = await postV1Companies({
        body: AdminCompany.deparse(company) as Company_Create,
      });

      return AdminCompany.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.company.failed_to_create'));
      throw error;
    }
  }, []);

  const updateCompany = useCallback(async (company: AdminCompany) => {
    try {
      const response = await patchV1CompaniesId({
        path: { id: company.id },
        body: AdminCompany.deparse(company) as Company_Update,
      });

      return AdminCompany.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.company.failed_to_update'));
      throw error;
    }
  }, []);

  // META request to handle case, if company has been created but LOGO POST REQUEST is failed.
  // In this case the safest way is to update just created company instead of creating new one with the same data OR operate with old data
  const postCompanyUpdate = useCallback(
    (company: AdminCompany) =>
      company.id ? updateCompany(company) : createCompany(company),
    [createCompany, updateCompany],
  );

  const deleteCompany = useCallback(async (id: string) => {
    try {
      await deleteV1CompaniesId({ path: { id } });
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.company.failed_to_delete'));
      throw error;
    }
  }, []);

  const uploadLogo = useCallback(
    async ({ id, file }: UploadLogoParams) => {
      try {
        const directUpload = await createDirectUpload(file);
        const resp = await postV1CompaniesCompanyIdLogo({
          path: { 'company-id': id },
          body: { logo: directUpload.signed_id },
        });

        return AdminCompany.parse(resp.data.data);
      } catch (error) {
        if (isUploadValidationError(error)) {
          throw error;
        }
        connection.handleRequestError(
          error,
          t('error_messages.company.failed_to_upload_logo'),
        );
        throw error;
      }
    },
    [createDirectUpload],
  );

  return {
    deleteCompany,
    getAllCompanies,
    getCompanyById,
    postCompanyUpdate,
    uploadLogo,
  } as const;
}
